<template>
  <div class="employee-engagement">
    <h5 class="employee-engagement__title">Employee engagement</h5>
    <b-row class="employee-engagement__row">
      <b-col cols="12" md="4">
        <div class="employee-engagement__card">
          <div class="employee-engagement__content">
            <p class="employee-engagement__label">Recognition sent</p>
            <b-spinner v-if="engagement === undefined" small />
            <template v-else>
              <p class="employee-engagement__value">{{ engagement?.recognition_sent ?? 0 }}</p>
              <div v-if="engagement?.recognition_sent_change_pct !== undefined" class="employee-engagement__change-row">
                <span class="employee-engagement__change" :class="changeClass(engagement.recognition_sent_change_pct)">
                  {{ formatChange(engagement.recognition_sent_change_pct) }}
                </span>
                <span class="employee-engagement__change-text">{{
                  changeText(engagement.recognition_sent_change_pct)
                }}</span>
              </div>
            </template>
          </div>
          <div v-if="engagement?.recognition_sent_trend?.length" class="employee-engagement__chart">
            <line-chart
              :datasets="{ values: engagement.recognition_sent_trend }"
              :labels="{ values: engagement.recognition_sent_trend.map((_, i) => i) }"
              :chart-options="sparklineOptions"
              :display-points="false"
              fill-gradient
            />
          </div>
        </div>
      </b-col>

      <b-col cols="12" md="4">
        <div class="employee-engagement__card">
          <div class="employee-engagement__content">
            <p class="employee-engagement__label">Nominations received</p>
            <b-spinner v-if="engagement === undefined" small />
            <template v-else>
              <p class="employee-engagement__value">{{ engagement?.nominations_received ?? 0 }}</p>
              <div
                v-if="engagement?.nominations_received_change_pct !== undefined"
                class="employee-engagement__change-row"
              >
                <span
                  class="employee-engagement__change"
                  :class="changeClass(engagement.nominations_received_change_pct)"
                >
                  {{ formatChange(engagement.nominations_received_change_pct) }}
                </span>
                <span class="employee-engagement__change-text">{{
                  changeText(engagement.nominations_received_change_pct)
                }}</span>
              </div>
            </template>
          </div>
          <div v-if="engagement?.nominations_received_trend?.length" class="employee-engagement__chart">
            <line-chart
              :datasets="{ values: engagement.nominations_received_trend }"
              :labels="{ values: engagement.nominations_received_trend.map((_, i) => i) }"
              :chart-options="sparklineOptions"
              :display-points="false"
              fill-gradient
            />
          </div>
        </div>
      </b-col>

      <b-col cols="12" md="4">
        <div class="employee-engagement__card">
          <div class="employee-engagement__content">
            <p class="employee-engagement__label">Employee participation</p>
            <b-spinner v-if="engagement === undefined" small />
            <template v-else>
              <p class="employee-engagement__value">{{ engagement?.employee_participation ?? 0 }}%</p>
              <div
                v-if="engagement?.employee_participation_change_pct !== undefined"
                class="employee-engagement__change-row"
              >
                <span
                  class="employee-engagement__change"
                  :class="changeClass(engagement.employee_participation_change_pct)"
                >
                  {{ formatChange(engagement.employee_participation_change_pct) }}
                </span>
                <span class="employee-engagement__change-text">{{
                  changeText(engagement.employee_participation_change_pct)
                }}</span>
              </div>
            </template>
          </div>
          <div v-if="engagement?.employee_participation_trend?.length" class="employee-engagement__chart">
            <line-chart
              :datasets="{ values: engagement.employee_participation_trend }"
              :labels="{ values: engagement.employee_participation_trend.map((_, i) => i) }"
              :chart-options="sparklineOptions"
              :display-points="false"
              fill-gradient
            />
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script setup>
import LineChart, { CHART_PRESET_OPTIONS } from '@/components/charts/LineChart.vue';
import merge from 'lodash/merge';

defineProps({
  engagement: {
    type: Object,
    default: undefined,
  },
});

const sparklineOptions = merge({}, CHART_PRESET_OPTIONS.SPARKLINE, {
  aspectRatio: 2.8,
  plugins: {
    tooltip: { enabled: false },
  },
});

const changeClass = value => {
  if (value > 0) {
    return 'employee-engagement__change--up';
  }
  if (value < 0) {
    return 'employee-engagement__change--down';
  }
  return '';
};

const formatChange = value => {
  if (value > 0) {
    return `+${value}%`;
  }
  if (value < 0) {
    return `${value}%`;
  }
  return '0%';
};

const changeText = value => {
  if (value > 0) {
    return 'Up this month';
  }
  if (value < 0) {
    return 'Down this month';
  }
  return 'No change';
};
</script>

<style scoped lang="scss">
.employee-engagement {
  margin-bottom: 1.5rem;

  &__title {
    font-weight: 600;
    margin-bottom: 1rem;
    color: var(--palette-color-default);
  }

  &__row {
    --bs-gutter-x: 1rem;
    --bs-gutter-y: 1rem;
  }

  &__card {
    background-color: var(--palette-color-base-white);
    border-radius: 0.25rem;
    box-shadow: var(--depth-2);
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__content {
    padding: 1.25rem 1.5rem;
  }

  &__label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--palette-color-default);
    margin: 0 0 0.25rem;
  }

  &__chart {
    margin-top: auto;
  }

  &__value {
    font-size: 1.75rem;
    font-weight: 500;
    line-height: 1;
    margin: 0;
    color: var(--palette-color-default);
  }

  &__change-row {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-top: 0.5rem;
  }

  &__change {
    display: inline-flex;
    align-items: center;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    background-color: var(--palette-color-default-lighten-90);
    color: var(--palette-color-default-lighten-30);

    &--up {
      background-color: var(--palette-color-core-5-lighten);
      color: var(--palette-color-core-5);
    }

    &--down {
      background-color: var(--palette-color-core-1-lighten);
      color: var(--palette-color-core-1);
    }
  }

  &__change-text {
    font-size: 0.75rem;
    color: var(--palette-color-default-lighten-30);
  }
}
</style>
